import { DateTime, IANAZone } from 'luxon';
import { Option } from 'util/multi-select';
import { FilterTimeZone, FilterType, SearchParam } from './search.context';
import { Admin } from 'api/client-domain/client-domain.types';
import { getEmployeeAuthInfo } from 'api/client-domain/client-domain.utils';

type SearchParamOptions<T> = Omit<
  SearchParam<T>,
  'paramName' | 'value' | 'startValue' | 'endValue'
>;

export const searchParam = <T>(
  paramName: string,
  options: SearchParamOptions<T>,
): SearchParam<T> => {
  const toUrlStringWrapper = (value: T) => {
    // Guard against bad value conversions
    // == actually does some conversion we want
    if (value == null) return '';
    else if (
      typeof value === 'object' &&
      'length' in value &&
      (value as any).length === 0
    )
      return '';
    else return options.toUrlString(value);
  };

  return {
    paramName,
    ...options,
    toUrlString: toUrlStringWrapper,
    value: options.defaultValue,
  };
};

type SpecificSearchParamOptions<T> = {
  defaultValue: T;
  options?: Option[];
  asyncOptions?: (searchParamsValues: {
    [key: string]: any;
  }) => Promise<Option[]>;
  asyncDependents?: string[];
  filter?: FilterType;
  category?: string;
  query?: boolean;
  toQueryString?: (value: T) => string;
  timeZone?: FilterTimeZone;
  excludeFromQuery?: boolean;
  excludeFromUrl?: boolean;
  'data-testid'?: string;
  allOptionId?: string;
};

export const stringSearchParam = (
  paramName: string,
  options: SpecificSearchParamOptions<string>,
) =>
  searchParam(paramName, {
    ...options,
    toUrlString: value => value,
    parse: value => value,
  });

export const numberSearchParam = (
  paramName: string,
  options: SpecificSearchParamOptions<number>,
) =>
  searchParam(paramName, {
    ...options,
    toUrlString: value => value.toString(),
    parse: value => Number(value),
  });

export const booleanSearchParam = (
  paramName: string,
  options: SpecificSearchParamOptions<boolean>,
) =>
  searchParam(paramName, {
    ...options,
    toUrlString: value => value.toString(),
    parse: value => value === 'true',
  });

export const stringListSearchParam = (
  paramName: string,
  options: SpecificSearchParamOptions<string[]>,
) =>
  searchParam(paramName, {
    ...options,
    toUrlString: (value: string[]) => value.toString(),
    parse: value => (value === '' ? [] : value.split(',')),
  });

export const numberListSearchParam = (
  paramName: string,
  options: SpecificSearchParamOptions<number[]>,
) =>
  searchParam(paramName, {
    ...options,
    toUrlString: (value: number[]) => value.toString(),
    parse: value => (value === '' ? [] : value.split(',').map(v => Number(v))),
  });

export const defaultSearchParams = (
  defaultValues: {
    count?: number;
    page?: number;
    sortBy?: string;
    sortDirection?: string;
  } = {
    count: 10,
    page: 0,
  },
) => [
  numberSearchParam('count', {
    defaultValue: defaultValues.count,
  }),
  numberSearchParam('page', {
    defaultValue: defaultValues.page,
  }),
  stringSearchParam('sortBy', {
    defaultValue: defaultValues.sortBy,
  }),
  stringSearchParam('sortDirection', {
    defaultValue: defaultValues.sortDirection,
  }),
];

export const querySearchParam = (
  paramName: string,
  options: {
    defaultValue?: string;
    category?: string;
    excludeFromUrl?: boolean;
  } = {},
) =>
  stringSearchParam(paramName, { query: true, defaultValue: '', ...options });

export const luxonDateSearchParam = (
  paramName: string,
  options: SpecificSearchParamOptions<DateTime>,
) => {
  if (options.timeZone) {
    return searchParam(paramName, {
      ...options,
      toUrlString: (value: DateTime) => value.toISO({ includeOffset: false }),
      parse: value => (value === '' ? null : DateTime.fromISO(value)),
      toQueryString: (value: DateTime, searchParams) =>
        value
          .setZone(searchParams[options.timeZone.paramName].value as string, {
            keepLocalTime: true,
          })
          .toISO(),
    });
  } else {
    return searchParam(paramName, {
      ...options,
      toUrlString: (value: DateTime) => value.toISO(),
      parse: value => (value === '' ? null : DateTime.fromISO(value)),
    });
  }
};

export const timeZoneSearchParam = (
  paramName: string,
  options: SpecificSearchParamOptions<string>,
) =>
  searchParam(paramName, {
    excludeFromQuery: true,
    ...options,
    toUrlString: value => value,
    parse: value => IANAZone.create(value).name,
  });

export const excludeAllParams = (params: Record<string, any>) =>
  Object.entries(params).reduce((result, [key, value]) => {
    if (value !== 'ALL') {
      result[key] = value;
    }
    return result;
  }, {});

export const locationsToQueryString = (locations: string[], all: string) => {
  if (locations.includes('ALL')) return all;
  else return locations.toString();
};

export const gatedLocationsToQueryString = (
  locations: string[],
  admin: Admin,
  all: string,
) => {
  const authInfo = getEmployeeAuthInfo(admin);
  if (locations.includes('ALL')) {
    if (
      authInfo.isSuperAdmin ||
      authInfo.isClientAdmin ||
      authInfo.isWorkspaceAdmin
    )
      return all;
    else return authInfo.authorizedStores.toString();
  } else {
    return locations.toString();
  }
};
